import cx from 'classnames'
import type React from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { isFieldHighlighted } from '@helpers'

import type { TextFieldProps } from '../InputElements'
import { TextField } from '../InputElements'
import type { ReactHookFormFieldCommonProps } from './types'

export type ReactHookFormTextFieldProps = Pick<
  TextFieldProps,
  | 'autoFocus'
  | 'className'
  | 'dense'
  | 'disabled'
  | 'placeholder'
  | 'required'
  | 'value'
  | 'label'
  | 'id'
  | 'endAdornment'
  | 'startAdornment'
  | 'type'
  | 'onBlur'
  | 'onFocus'
> & {
  multiline?: never
  minRows?: never
  maxRows?: never
} & ReactHookFormFieldCommonProps

export function ReactHookFormTextField({
  className,
  disabled = false,
  highlighted = false,
  isLabelHighlighted = false,
  name,
  shouldUnregister = false,
  transform,
  ...rest
}: ReactHookFormTextFieldProps) {
  const { control } = useFormContext()
  const {
    field: { onBlur, ...field },
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ control, name, shouldUnregister })

  const hasError = !!error
  const isFieldDisabled = disabled || isSubmitting

  return (
    <TextField
      {...field}
      className={cx(className, { 'form-control-error': hasError })}
      error={hasError}
      helperText={error?.message ?? ''}
      highlighted={isFieldHighlighted(highlighted, field.value)}
      isLabelHighlighted={isLabelHighlighted}
      disabled={isFieldDisabled}
      id={name}
      {...(transform && {
        value: transform.input(field.value),
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          field.onChange(transform.output(event.target.value))
          transform.positioner?.(event, transform.output(event.target.value), transform.input(field.value))
        },
      })}
      {...rest}
    />
  )
}
